"use client";

import { useEffect } from "react";

const UPDATE_POLL_MS = 60 * 60 * 1000;

/**
 * Registers the production service worker and activates any update silently.
 * Renders nothing.
 *
 * There is deliberately no "update ready — refresh" prompt. Navigations are
 * network-first, so a freshly loaded page already IS the newest build; asking
 * that visitor to refresh asks them to reload the version they just received,
 * and the toast came back on every single page load until they did. The worker
 * is only a cache layer — swapping it under a loaded page changes nothing the
 * visitor can see — so the waiting worker is told to take over immediately.
 *
 * The one case the prompt genuinely covered is a tab that outlived a deploy and
 * lazily requests a chunk the new build dropped. That is handled by
 * `@/lib/chunk-reload` (wired up in `instrumentation-client.ts` and the error
 * boundaries), which recovers without depending on the visitor noticing a toast.
 */
export function SwRegister() {
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return;
    if (!("serviceWorker" in navigator)) return;

    let registration: ServiceWorkerRegistration | undefined;
    let intervalId = 0;

    /** Hand control to a worker that finished installing. */
    const activateWaiting = (reg: ServiceWorkerRegistration) => {
      reg.waiting?.postMessage({ type: "SKIP_WAITING" });
    };

    const checkForUpdates = () => {
      registration?.update().catch(() => {});
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") checkForUpdates();
    };

    const setup = async () => {
      registration =
        (await navigator.serviceWorker.getRegistration()) ??
        (await navigator.serviceWorker.register("/sw.js"));

      // A worker left waiting by an earlier visit would otherwise sit there
      // until every tab of the site closed.
      activateWaiting(registration);

      registration.addEventListener("updatefound", () => {
        const installing = registration?.installing;
        if (!installing) return;
        installing.addEventListener("statechange", () => {
          if (installing.state === "installed" && registration) {
            activateWaiting(registration);
          }
        });
      });

      checkForUpdates();
      document.addEventListener("visibilitychange", onVisibilityChange);
      intervalId = window.setInterval(checkForUpdates, UPDATE_POLL_MS);
    };

    const run = () => {
      setup().catch(() => {});
    };

    if (document.readyState === "complete") run();
    else window.addEventListener("load", run);

    return () => {
      window.removeEventListener("load", run);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, []);

  return null;
}
